import type {
  AssignmentListFilter,
  Page,
  RespondentAssignment,
  RespondentAssignmentDetail,
  SelfAssignmentRequest,
  SendAssignmentsRequest,
  TenantAssignment,
  TenantAssignmentDetail,
} from "./model"
import { baseApi, extractChannelEvent, subscribe } from "@/base"
import type { Uuid } from "@/utils"
import { invariant } from "@/utils"

const api = baseApi
  .enhanceEndpoints({ addTagTypes: ["Assignments", "MyProfiles"] })
  .injectEndpoints({
    endpoints: (build) => ({
      getAssignments: build.query<Page<TenantAssignment>, AssignmentListFilter>(
        {
          query: (params) => ({ url: "assignments", params }),
          providesTags: ["Assignments"],
          // The list advertises its tenant channel via the Realtime header;
          // any assignment change on it refetches the list (the mirror of
          // the notification slice's live subscription).
          onCacheEntryAdded: async (
            _arg,
            { cacheDataLoaded, cacheEntryRemoved, dispatch },
          ) => {
            const { meta } = await cacheDataLoaded
            invariant(meta?.response)
            const [channelName, eventName] = extractChannelEvent(meta.response)

            const unbind = await subscribe(
              channelName,
              () => dispatch(baseApi.endpoints.ablyAuth.initiate()).unwrap(),
              {
                [eventName]: () => {
                  dispatch(api.util.invalidateTags(["Assignments"]))
                },
              },
            )

            await cacheEntryRemoved
            unbind()
          },
        },
      ),
      getAssignment: build.query<TenantAssignmentDetail, Uuid>({
        query: (id) => `assignments/${id}`,
        providesTags: ["Assignments"],
      }),
      sendAssignments: build.mutation<
        Page<TenantAssignment>,
        SendAssignmentsRequest
      >({
        query: (body) => ({
          url: "assignments",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Assignments"],
      }),
      createSelfAssignment: build.mutation<
        TenantAssignment,
        SelfAssignmentRequest
      >({
        query: (body) => ({
          url: "assignments/self",
          method: "POST",
          body,
        }),
        invalidatesTags: ["Assignments", "MyProfiles"],
      }),
      revokeAssignment: build.mutation<TenantAssignment, Uuid>({
        query: (id) => ({
          url: `assignments/${id}/revoke`,
          method: "POST",
        }),
        invalidatesTags: ["Assignments", "MyProfiles"],
      }),
      remindAssignment: build.mutation<TenantAssignment, Uuid>({
        query: (id) => ({
          url: `assignments/${id}/remind`,
          method: "POST",
        }),
        invalidatesTags: ["Assignments"],
      }),
      getMyProfiles: build.query<Page<RespondentAssignment>, void>({
        query: () => "myprofiles",
        providesTags: ["MyProfiles"],
      }),
      getMyProfile: build.query<RespondentAssignmentDetail, Uuid>({
        query: (id) => `myprofiles/${id}`,
        providesTags: ["MyProfiles"],
      }),
    }),
  })

export const {
  useGetAssignmentsQuery,
  useGetAssignmentQuery,
  useSendAssignmentsMutation,
  useCreateSelfAssignmentMutation,
  useRevokeAssignmentMutation,
  useRemindAssignmentMutation,
  useGetMyProfilesQuery,
  useGetMyProfileQuery,
} = api

export default api
